import { GeographicCoordinates, RouteStreetSegment } from '@/api/navigationApi/types.gen'
import type { LayerSpecification, Map as MapboxMap, MapEvent } from 'mapbox-gl'

const PRAGUE_CENTER = { longitude: 14.41204, latitude: 50.01079 }
const DEFAULT_GARAGE = { longitude: 14.401332, latitude: 50.070575 }

const determineMapCenter = (): GeographicCoordinates => {
  const cityCoordinates =
    import.meta.env.VITE_CITY_COORDINATES || (typeof window !== 'undefined' ? window.ENV?.CITY_COORDINATES : undefined)

  if (!cityCoordinates) return PRAGUE_CENTER

  const [longitude, latitude] = cityCoordinates.split(':').map(Number)

  return { longitude, latitude }
}

const determineGarageCoordinates = (): GeographicCoordinates => {
  const raw =
    import.meta.env.VITE_GARAGE_COORDINATES ||
    (typeof window !== 'undefined' ? window.ENV?.GARAGE_COORDINATES : undefined)

  if (!raw) return DEFAULT_GARAGE

  const [longitude, latitude] = raw.split(':').map(Number)

  // Guards against unset/malformed values, including an unreplaced `__GARAGE_COORDINATES__` placeholder.
  if (Number.isNaN(longitude) || Number.isNaN(latitude)) return DEFAULT_GARAGE

  return { longitude, latitude }
}

const reduceGrassOpacity = (map: MapboxMap) => {
  const layers = map.getStyle().layers
  layers?.forEach((layer: LayerSpecification) => {
    if (layer.type === 'fill') {
      const layerId = layer.id.toLowerCase()
      const isGrassLayer =
        (layerId.includes('park') ||
          layerId.includes('landcover') ||
          layerId.includes('natural') ||
          layerId.includes('vegetation') ||
          layerId.includes('grass') ||
          layerId.includes('landuse') ||
          layerId.includes('green') ||
          layerId.includes('forest') ||
          layerId.includes('wood') ||
          layerId.includes('tree')) &&
        !layerId.includes('road') &&
        !layerId.includes('building') &&
        !layerId.includes('water') &&
        !layerId.includes('bridge')

      if (isGrassLayer) {
        try {
          map.setPaintProperty(layer.id, 'fill-opacity', 0.3)
        } catch {
          // Layer might not support fill-opacity
        }
      }
    }
  })
}

const onMapLoad = (e: MapEvent) => {
  const map = e.target

  const allLayers = map.getStyle().layers

  if (allLayers) {
    allLayers.forEach((layer: LayerSpecification) => {
      const id = layer.id.toLowerCase()

      // Handle symbol layers
      if (layer.type === 'symbol') {
        map.setLayoutProperty(id, 'icon-allow-overlap', true)
        map.setLayoutProperty(id, 'text-allow-overlap', true)
        map.setLayoutProperty(id, 'icon-ignore-placement', true)
        map.setLayoutProperty(id, 'text-ignore-placement', true)

        if (id === 'arrowhead' || id === 'directionarrow') return

        if (id.includes('road') || id.includes('street')) return

        // Remove everything else (Parks, POIs, Transit, etc.)
        // This includes 'natural-point-label', 'poi-label', 'park-label'
        map.removeLayer(layer.id)
      }
    })

    // Reduce grass opacity immediately
    reduceGrassOpacity(map)

    // Also try after a short delay to ensure style is fully loaded
    setTimeout(() => {
      reduceGrassOpacity(map)
    }, 100)
  }
}

// Helper to create a stable hash of array contents
// Differentiates between undefined and empty array to prevent memo cache issues
const createArrayHash = (segments: RouteStreetSegment[] | undefined): string => {
  if (segments === undefined) return '__UNDEFINED__'
  if (segments.length === 0) return '__EMPTY__'
  return segments.map(s => s.street_segment_id).join(',')
}

// Helper to create a stable hash of current segment
const createCurrentSegmentHash = (segment: RouteStreetSegment | undefined): string => {
  if (!segment) return ''
  return `${segment.street_segment_id}-${segment.direction_forward}`
}

export { determineMapCenter, determineGarageCoordinates, onMapLoad, createArrayHash, createCurrentSegmentHash }
